/**
 * TosReAcceptModal - Blocking modal for re-acceptance of updated Terms of Service.
 *
 * Displays a full-viewport overlay with change summary, checkbox, and submit/logout controls.
 * Rendered by TosGate when the user's accepted version is lower than the active version.
 *
 * Features:
 * - Full-viewport overlay with aria-modal="true" and focus trap
 * - Displays change summary in user's languagePreference
 * - Link to /terms with target="_blank"
 * - Checkbox and "Agree and continue" button (disabled until checked)
 * - "Logout" button that ends session and returns to /
 * - On submit: calls POST /auth/accept-tos; on success dismisses modal; on error shows inline error
 * - Blocks navigation via useBlocker while modal is active
 *
 * Requirements: 5.2, 5.3, 5.4, 5.5, 5.6, 5.7, 5.8, 5.9, 5.10, 9.3, 9.4
 */

import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import apiClient, { isApiError } from '@/services/api';
import { isLiff } from '@/services/environment';
import { openExternal } from '@/services/liff';

/**
 * Opens the Terms page without trapping LIFF users in a reload loop.
 * In LINE LIFF, target="_blank" reboots the LIFF app; hand the URL to the
 * system browser instead. In a normal browser, open a new tab.
 */
function openLegalLink(path: string): void {
  const url = `${window.location.origin}${path}`;
  if (isLiff() && openExternal(url)) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

interface TosReAcceptModalProps {
  children: ReactNode;
}

interface ChangeSummary {
  th: string;
  en: string;
}

export function TosReAcceptModal({ children: _children }: TosReAcceptModalProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { activeTosVersion, logout, user } = useAuth();

  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [changeSummary, setChangeSummary] = useState<ChangeSummary | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  // Focus trap refs
  const modalRef = useRef<HTMLDivElement>(null);

  // Block navigation while modal is active (Req 5.9)
  useBlocker(() => !dismissed);

  // Determine user's language preference
  const language: 'th' | 'en' = (user as { languagePreference?: string } | null)
    ?.languagePreference === 'en'
    ? 'en'
    : i18n.language?.startsWith('en')
      ? 'en'
      : 'th';

  // Fetch change summary from GET /tos/active (Req 5.2, 9.3, 9.4)
  useEffect(() => {
    let cancelled = false;

    async function fetchChangeSummary() {
      try {
        const response = await apiClient.get<{
          changeSummary: ChangeSummary;
        }>('/tos/active');
        if (!cancelled) {
          setChangeSummary(response.data.changeSummary);
        }
      } catch {
        // Non-fatal: modal still functions without change summary
      } finally {
        if (!cancelled) {
          setIsLoadingSummary(false);
        }
      }
    }

    fetchChangeSummary();
    return () => {
      cancelled = true;
    };
  }, []);

  // Focus trap: trap focus within the modal (accessibility)
  useEffect(() => {
    if (dismissed) return;

    // Focus the modal on mount
    modalRef.current?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;

      const focusableElements = modalRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );

      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0] as HTMLElement | undefined;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement | undefined;

      if (!firstElement || !lastElement) return;

      if (e.shiftKey) {
        // Shift+Tab: if focus is on first element, wrap to last
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: if focus is on last element, wrap to first
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [dismissed]);

  // Handle accept submission (Req 5.7, 5.8, 5.10)
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!agreed || !activeTosVersion) return;

      setIsSubmitting(true);
      setError(null);

      try {
        await apiClient.post('/auth/accept-tos', { version: activeTosVersion });
        // On success: dismiss modal (Req 5.8)
        setDismissed(true);
        // Force page reload to refresh auth state with new acceptedTosVersion
        window.location.reload();
      } catch (err: unknown) {
        if (isApiError(err) && err.code === 'TOS_VERSION_MISMATCH') {
          setError(t('tos.errorVersionMismatch'));
        } else {
          setError(t('tos.errorGeneric'));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [agreed, activeTosVersion, t],
  );

  // Handle logout (Req 5.6)
  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } finally {
      // Navigate to landing page regardless of logout success
      navigate('/', { replace: true });
    }
  }, [logout, navigate]);

  // If modal has been dismissed (acceptance succeeded), render children
  if (dismissed) {
    return null;
  }

  const summaryText = changeSummary?.[language] ?? changeSummary?.en ?? '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tos-reaccept-title"
      ref={modalRef}
      tabIndex={-1}
    >
      <div className="mx-4 w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl sm:p-8">
        <h2
          id="tos-reaccept-title"
          className="mb-2 text-xl font-bold text-gray-900"
        >
          {language === 'th' ? 'ข้อกำหนดการใช้งานได้รับการอัปเดต' : 'Terms of Service Updated'}
        </h2>

        <p className="mb-4 text-sm text-gray-600">
          {language === 'th'
            ? 'กรุณาอ่านการเปลี่ยนแปลงและยอมรับข้อกำหนดการใช้งานฉบับใหม่เพื่อใช้งานต่อ'
            : 'Please review the changes and accept the updated Terms of Service to continue.'}
        </p>

        {/* Change summary (Req 5.2, 9.3, 9.4) */}
        {isLoadingSummary ? (
          <div className="mb-4 animate-pulse rounded-lg bg-gray-100 p-4">
            <div className="h-4 w-3/4 rounded bg-gray-200" />
          </div>
        ) : summaryText ? (
          <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 p-4">
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-700">
              {language === 'th' ? 'สรุปการเปลี่ยนแปลง' : 'What Changed'}
            </h3>
            <p className="text-sm text-blue-900">{summaryText}</p>
          </div>
        ) : null}

        {/* Link to full terms (Req 5.3) */}
        <a
          href="/terms"
          onClick={(e) => {
            e.preventDefault();
            openLegalLink('/terms');
          }}
          target="_blank"
          rel="noopener noreferrer"
          className="mb-6 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline"
        >
          {language === 'th' ? 'อ่านข้อกำหนดการใช้งานฉบับเต็ม' : 'Read the full Terms of Service'}
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </a>

        <form onSubmit={handleSubmit}>
          {/* Checkbox (Req 5.4, 5.5) */}
          <label className="mb-6 flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">
              {language === 'th'
                ? 'ฉันได้อ่านและยอมรับข้อกำหนดการใช้งานฉบับอัปเดต'
                : 'I have read and agree to the updated Terms of Service'}
            </span>
          </label>

          {/* Error display (Req 5.10) */}
          {error && (
            <div
              role="alert"
              className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
            {/* Agree and continue button (Req 5.5, 5.7) */}
            <button
              type="submit"
              disabled={!agreed || isSubmitting}
              className="min-h-[44px] flex-1 rounded-lg bg-primary-600 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting
                ? (language === 'th' ? 'กำลังยอมรับ...' : 'Accepting...')
                : (language === 'th' ? 'ยอมรับและดำเนินการต่อ' : 'Agree and Continue')}
            </button>

            {/* Logout button (Req 5.6) */}
            <button
              type="button"
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="min-h-[44px] rounded-lg border border-gray-300 bg-white px-6 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoggingOut
                ? (language === 'th' ? 'กำลังออกจากระบบ...' : 'Logging out...')
                : (language === 'th' ? 'ออกจากระบบ' : 'Logout')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
